/**
 * Free Shipping Progress Bar
 * Dynamically updates progress bars when cart changes
 */
(function() {
  // Configuration
  const CONFIG = {
    DEFAULT_THRESHOLD: 5000, // 50€ in cents
    CACHE_DURATION: 1000, // 1 second
    SELECTORS: {
      BAR: '.free-shipping-bar',
      PROGRESS: '.free-shipping-bar__progress',
      FILL: '.free-shipping-bar__progress-fill',
      MESSAGE: '.free-shipping-bar__message',
      TEXT: '.free-shipping-bar__message-text'
    }
  };

  // Simple cache to reduce API calls
  let cartCache = null;
  let cacheTimestamp = 0;

  /**
   * Get cart data with caching
   * @returns {Promise<Object>} Cart data
   */
  function getCachedCart() {
    const now = Date.now();
    if (cartCache && (now - cacheTimestamp) < CONFIG.CACHE_DURATION) {
      return Promise.resolve(cartCache);
    }
    
    return fetch('/cart.js')
      .then(response => {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .then(data => {
        cartCache = data;
        cacheTimestamp = now;
        return data;
      });
  }

  /**
   * Update all progress bars on the page
   */
  function updateAllProgressBars() {
    getCachedCart()
      .then(cart => {
        const bars = document.querySelectorAll(CONFIG.SELECTORS.BAR);
        
        bars.forEach(bar => {
          try {
            updateProgressBar(bar, cart);
          } catch (error) {
            console.error('Error updating progress bar:', error);
          }
        });
      })
      .catch(error => console.error('Error fetching cart:', error));
  }

  /**
   * Update a single progress bar
   * @param {HTMLElement} bar - Progress bar container
   * @param {Object} cart - Cart data
   */
  function updateProgressBar(bar, cart) {
    const threshold = Math.max(0, parseInt(bar.dataset.threshold) || CONFIG.DEFAULT_THRESHOLD);
    const cartTotal = cart.total_price;
    const remaining = threshold - cartTotal;
    
    // Get messages from data attributes (from settings)
    const messageTemplate = bar.dataset.messageTemplate || 'Osta vielä {remaining} arvosta niin saat ilmaisen toimituksen! 🎉';
    const successMessage = bar.dataset.successMessage || 'Hienoa! Saat ilmaisen toimituksen! ✓';
    
    // Calculate percentage
    let progressPercent = Math.min(100, Math.max(0, (cartTotal / threshold) * 100));
    
    // Update progress bar
    updateProgressFill(bar, progressPercent);
    
    // Update message
    updateMessage(bar, remaining, messageTemplate, successMessage);
  }

  /**
   * Update progress fill width and ARIA
   * @param {HTMLElement} bar - Progress bar container
   * @param {number} progressPercent - Progress percentage
   */
  function updateProgressFill(bar, progressPercent) {
    const progressContainer = bar.querySelector(CONFIG.SELECTORS.PROGRESS);
    const progressFill = bar.querySelector(CONFIG.SELECTORS.FILL);
    
    if (!progressFill) {
      console.warn('Progress fill element not found');
      return;
    }
    
    // Update only dynamic properties
    progressFill.style.width = `${progressPercent}%`;
    progressFill.dataset.progress = Math.round(progressPercent);
    
    // Update ARIA attributes
    if (progressContainer) {
      progressContainer.setAttribute('aria-valuenow', Math.round(progressPercent));
    }
  }

  /**
   * Update message text
   * @param {HTMLElement} bar - Progress bar container
   * @param {number} remaining - Remaining amount in cents
   * @param {string} messageTemplate - Message template
   * @param {string} successMessage - Success message
   */
  function updateMessage(bar, remaining, messageTemplate, successMessage) {
    const messageEl = bar.querySelector(CONFIG.SELECTORS.MESSAGE);
    const messageText = bar.querySelector(CONFIG.SELECTORS.TEXT);
    
    if (!messageText) {
      console.warn('Message text element not found');
      return;
    }
    
    if (remaining > 0) {
      const remainingMoney = formatMoney(remaining);
      const messageContent = messageTemplate.replace('{remaining}', remainingMoney);
      messageText.textContent = messageContent;
      
      if (messageEl) {
        messageEl.classList.remove('free-shipping-bar__message--success');
      }
    } else {
      messageText.textContent = successMessage;
      
      if (messageEl) {
        messageEl.classList.add('free-shipping-bar__message--success');
      }
    }
  }

  /**
   * Format cents to Finnish currency format
   * @param {number} cents - Amount in cents
   * @returns {string} Formatted money string
   */
  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAllProgressBars);
  } else {
    updateAllProgressBars();
  }

  // Listen for cart updates
  document.addEventListener('cart:updated', updateAllProgressBars);
  document.addEventListener('cart:refresh', updateAllProgressBars);
  document.addEventListener('cart:items-changed', updateAllProgressBars);
})();
